import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import * as IoC from 'typescript-ioc';
import { JobRunner } from '../job-runner';
import { JobDtoType } from '../index';
import { JobType } from '../../../api/jobs.dao';
import { JobFinishedResultType, JobMapper, JobTriggeredResultType } from './job-mapper';
import { AuthAggregateProvider } from '../../auth/implementations/auth-aggregate-provider';
import { BlueprintJobMixpanelMapper, BlueprintJobMixpanelProperties } from './blueprint-job-mixpanel.mapper';

export class JobTrackingService {
    private static readonly trackableGlobalContextJobTypeNameMap: Map<JobType, string> = new Map([
        [JobType.DeleteEvent, 'Delete Event']
    ]);

    private static getGlobalContextJobNameByType(type: JobType): string {
        return JobTrackingService.trackableGlobalContextJobTypeNameMap.get(type) || '';
    }
    private static isGlobalContextJobTrackable(job: JobDtoType): boolean {
        return JobTrackingService.trackableGlobalContextJobTypeNameMap.has(job.jobType);
    }

    private static isEventContextJob(job: JobRunner | JobDtoType): boolean {
        return job instanceof JobRunner;
    }

    private static getJobType(job: JobRunner | JobDtoType): JobType {
        return job instanceof JobRunner ? job.type : job.jobType;
    }

    private static toJobDto(job: JobRunner | JobDtoType): JobDtoType {
        return job instanceof JobRunner ? JobMapper.toDto(job) : job;
    }

    public trackJobTriggerResult(job: JobRunner | JobDtoType): void {
        if (BlueprintJobMixpanelMapper.isBlueprintJob(JobTrackingService.getJobType(job))) {
            return;
        }

        if (JobTrackingService.isEventContextJob(job)) {
            this.trackEventContextJobTriggerResult(job as JobRunner);
        } else {
            this.trackGlobalContextJobTriggerResult(job as JobDtoType);
        }
    }

    public trackJobExecutionResult(job: JobRunner | JobDtoType): void {
        if (BlueprintJobMixpanelMapper.isBlueprintJob(JobTrackingService.getJobType(job))) {
            this.trackBlueprintJobExecutionResult(JobTrackingService.toJobDto(job))
                // eslint-disable-next-line no-empty-function
                .catch(() => {});
            return;
        }

        if (JobTrackingService.isEventContextJob(job)) {
            this.trackEventContextJobExecutionResult(job as JobRunner);
        } else {
            this.trackGlobalContextJobExecutionResult(job as JobDtoType);
        }
    }

    constructor(
        private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        private readonly authProvider: AuthAggregateProvider = IoC.Container.get(AuthAggregateProvider)
    ) {}

    private track(
        jobName: string,
        props: JobTriggeredResultType | JobFinishedResultType | BlueprintJobMixpanelProperties
    ): void {
        void this.trackingService.get().track(jobName, props);
    }

    private trackEventContextJobTriggerResult(job: JobRunner): void {
        this.track(job.name, JobMapper.toJobTriggeredDto(job));
    }
    private trackEventContextJobExecutionResult(job: JobRunner): void {
        this.track(job.name, JobMapper.toJobFinishedDto(job));
    }

    private trackGlobalContextJobTriggerResult(job: JobDtoType): void {
        if (JobTrackingService.isGlobalContextJobTrackable(job)) {
            this.track(
                JobTrackingService.getGlobalContextJobNameByType(job.jobType),
                JobMapper.dtoToTriggeredResultType(job)
            );
        }
    }

    private trackGlobalContextJobExecutionResult(job: JobDtoType): void {
        if (JobTrackingService.isGlobalContextJobTrackable(job)) {
            this.track(JobTrackingService.getGlobalContextJobNameByType(job.jobType), job);
        }
    }

    private async trackBlueprintJobExecutionResult(job: JobDtoType): Promise<void> {
        const eventName = BlueprintJobMixpanelMapper.getEventName(job.jobType);
        if (!eventName) {
            return;
        }

        const user = await this.resolveCurrentUser();
        const props = BlueprintJobMixpanelMapper.toMixpanelProperties(job, user);

        await this.trackingService.get().track(eventName, props);
    }

    private async resolveCurrentUser(): Promise<{ email: string }> {
        try {
            const currentUser = await this.authProvider.get().getCurrentUser();

            return {
                email: currentUser.email ?? '',
            };
        } catch {
            return { email: '' };
        }
    }
}
